import { useState } from "react";
import { useLocalProfile } from "../local/useLocalProfile";
import { saveProfile, type LocalProfile } from "../local/profile";
import {
  createPlayerOnServer,
  loadPlayerFromServer,
} from "../local/playerAccount";

/**
 * Minimal sign-in / create-account modal. Two tabs:
 *   - Sign In: paste a 4-digit code → fetch the saved profile.
 *   - Create: turn the current guest profile into a real account by asking
 *     the server for a code and storing it on the profile.
 * If either path fails the modal stays open with an error; the user can
 * dismiss to keep playing as a guest.
 */
export function AuthModal({ onClose }: { onClose: () => void }) {
  const { profile, setProfile } = useLocalProfile();
  const [tab, setTab] = useState<"signin" | "create">("signin");
  const [code, setCode] = useState("");
  const [name, setName] = useState(profile?.displayName ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    setError(null);
    const trimmed = code.trim();
    if (!/^\d{4}$/.test(trimmed)) {
      setError("Code must be 4 digits.");
      return;
    }
    setBusy(true);
    try {
      const loaded = await loadPlayerFromServer(trimmed);
      if (!loaded) {
        setError("No account found for that code.");
        return;
      }
      const profileWithCode: LocalProfile = { ...loaded, code: trimmed };
      saveProfile(profileWithCode);
      setProfile(profileWithCode);
      onClose();
    } catch {
      setError("Couldn't sign in. Try again or keep playing as a guest.");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      setError("Please enter a name (at least 2 characters).");
      return;
    }
    if (!profile) {
      setError("Profile not ready — try refreshing.");
      return;
    }
    setBusy(true);
    try {
      const renamed: LocalProfile = { ...profile, displayName: trimmedName };
      const { code: newCode } = await createPlayerOnServer(renamed);
      const next: LocalProfile = { ...renamed, code: newCode };
      saveProfile(next);
      setProfile(next);
      onClose();
    } catch {
      setError("Couldn't create account. Keep playing as a guest for now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-modal-overlay" role="dialog" aria-modal="true" aria-label="Sign in or create account" onClick={onClose}>
      <div className="auth-modal-card" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
        <button className="popup-close-x" onClick={onClose} aria-label="Close">×</button>
        <h2>Welcome to Word Runner</h2>
        <p className="t-muted" style={{ marginTop: 0 }}>
          Save your progress to play across devices, or keep going as a guest.
        </p>

        <div className="auth-modal-tabs">
          <button
            className={tab === "signin" ? "is-active" : ""}
            onClick={() => { setTab("signin"); setError(null); }}
          >
            Sign In
          </button>
          <button
            className={tab === "create" ? "is-active" : ""}
            onClick={() => { setTab("create"); setError(null); }}
          >
            Create Account
          </button>
        </div>

        {tab === "signin" ? (
          <div className="auth-modal-body">
            <label className="auth-modal-label" htmlFor="auth-code">Your 4-digit code</label>
            <input
              id="auth-code"
              className="auth-modal-input"
              inputMode="numeric"
              pattern="\d*"
              maxLength={4}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="1234"
              disabled={busy}
            />
            <button
              className="btn btn-primary btn-big btn-block"
              onClick={signIn}
              disabled={busy || code.length < 4}
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </div>
        ) : (
          <div className="auth-modal-body">
            <label className="auth-modal-label" htmlFor="auth-name">Your player name</label>
            <input
              id="auth-name"
              className="auth-modal-input"
              maxLength={20}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex"
              disabled={busy}
            />
            <p className="t-caption t-muted" style={{ marginTop: -4 }}>
              We'll give you a 4-digit code so you can sign back in on another device.
            </p>
            <button
              className="btn btn-primary btn-big btn-block"
              onClick={create}
              disabled={busy || name.trim().length < 2}
            >
              {busy ? "Creating…" : "Create Account"}
            </button>
          </div>
        )}

        {error && <div className="auth-modal-error">{error}</div>}

        <button className="auth-modal-guest" onClick={onClose}>
          Keep playing as a guest →
        </button>
      </div>
    </div>
  );
}
